<template>
	<section class="create-guest-form">
		<!-- Headlines and go back link -->
		<div v-if="modalView" class="grid-x grid-margin-x">
			<div class="cell small-12">	
				<h3 class="create-guest-form__title">
					<svg data-name="Lager 1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" v-bind:svg-inline="''" v-bind:class="'icon'" v-bind:focusable="'false'" v-bind:role="'presentation'" v-bind:tabindex="'-1'"><path class="cls-1" d="M14.3 14.06l-.18-.06a7.1 7.1 0 003-6 5.07 5.07 0 00-5-5 5.07 5.07 0 00-5 5 7.11 7.11 0 003 6l-.18.09c-3.37 1.5-6.86 3-5.79 6.26l.22.68h15.5l.22-.68c1.07-3.24-2.42-4.79-5.79-6.29zM9.14 8a3 3 0 016 0c0 2.07-.89 3.59-3 4.92-2.14-1.33-3-2.85-3-4.92zM6 19c.18-.77 1.31-1.57 4.77-3.11.48-.22.92-.43 1.34-.66.42.23.87.44 1.35.66 3.54 1.54 4.62 2.34 4.79 3.11z"/></svg>
					<template v-if="edit">
						<span>{{ $t('companion.updateCompanionHeading') }}</span>
					</template>
					<template v-else>
						<span>{{ $t('companion.createCompanionHeading') }}</span>
					</template>
				</h3>	
			</div>
		</div>

		<!-- Personal section -->
		<template v-if="modalView">
			<AccordionFieldset
				v-on:toggle="
					() => {
						this.showPersonalSection = !this.showPersonalSection
					}
				"
				:title="$t('companion.personalHeading')"
				:hasMandatoryFields="true"
				:missingFields="countErrors(
					[
						this.validation.name.error, 
						this.validation.lastname.error, 
						this.validation.birthdate.error
					],
					[
						guest.name,
						guest.lastname,
						guest.birthdate
					]
				)"
				:show="this.showPersonalSection"
				icon="pen"
			>
				<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<!-- Firstname input -->
					<div class="cell small-12 medium-6">
						<InputComponent
							:label="$t('newaccount.firstNamePlaceholder') + ':'"
							:mandatory="true"
							:placeholderValue="$t('newaccount.firstNamePlaceholder')"
							v-model="guest.name"
							:hasError="validation.name.error"
							:errorMessage="validation.name.message"
							:lightTheme="true"
							@input-enter="retry()"
						></InputComponent>
					</div>

					<!-- Lastname input -->
					<div class="cell small-12 medium-6">
						<InputComponent
							:label="$t('newaccount.lastNamePlaceholder') + ':'"
							:mandatory="true"
							:placeholderValue="$t('newaccount.lastNamePlaceholder')"
							v-model="guest.lastname"
							:hasError="validation.lastname.error"
							:errorMessage="validation.lastname.message"
							:lightTheme="true"
							@input-enter="retry()"
						></InputComponent>
					</div>

					<!-- Birt Date input -->
					<div class="cell small-12">
						<BirthDate
							:label="$t('newaccount.birthdate') + ':'"
							:mandatory="true"
							:getDate="guest.birthdate"
							:hasError="validation.birthdate.error"
							:errorMessage="validation.birthdate.message"
							:isLocked="edit"
							:lightTheme="true"
							@getBirthdate="onChangeDate"
						/>
					</div>
				</div>
			</AccordionFieldset>
		</template>
		<template v-else>
			<div class="create-guest-form__wrapper grid-x grid-margin-x">
				<div class="cell small-12">
					<h4 class="create-guest-form__headline create-guest-form__headline--no-margin">
						{{ $t('companion.personalHeading') }}
					</h4>
				</div>

				<!-- Firstname input -->
				<div class="cell small-12 medium-6">
					<InputComponent
						:label="$t('newaccount.firstNamePlaceholder') + ':'"
						:mandatory="true"
						:placeholderValue="$t('newaccount.firstNamePlaceholder')"
						v-model="guest.name"
						:hasError="validation.name.error"
						:errorMessage="validation.name.message"
						:lightTheme="false"
						@input-enter="retry()"
					></InputComponent>
				</div>

				<!-- Lastname input -->
				<div class="cell small-12 medium-6">
					<InputComponent
						:label="$t('newaccount.lastNamePlaceholder') + ':'"
						:mandatory="true"
						:placeholderValue="$t('newaccount.lastNamePlaceholder')"
						v-model="guest.lastname"
						:hasError="validation.lastname.error"
						:errorMessage="validation.lastname.message"
						:lightTheme="false"
						@input-enter="retry()"
					></InputComponent>
				</div>

				<!-- Birt Date input -->
				<div class="cell small-12">
					<BirthDate
						:label="$t('newaccount.birthdate') + ':'"
						:mandatory="true"
						:getDate="guest.birthdate"
						:hasError="validation.birthdate.error"
						:errorMessage="validation.birthdate.message"
						:isLocked="edit"
						:lightTheme="false"
						@getBirthdate="onChangeDate"
					/>
				</div>
			</div>
		</template>		

		<!-- Size section -->
		<template v-if="modalView">
			<AccordionFieldset
				v-on:toggle="
					() => {
						this.showSizeSection = !this.showSizeSection
					}
				"
				:title="$t('companion.profileHeading')"
				:show="this.showSizeSection"
				:hasMandatoryFields="skirental"
				:missingFields="countErrors(
					[
						this.validation.length.error, 
						this.validation.weight.error, 
						this.validation.shoeSize.error
					],
					[
						guest.length,
						guest.weight,
						guest.shoesize
					]
				)"
				icon="pen"
				:small="true"
				:lightTheme="true"
			>
				<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12 medium-4">
						<NumberComponent
							:label="$t('newaccount.length') + ':'"
							:placeholderValue="'180'"
							:mandatory="skirental"
							:hideButtons="true"
							:minNumber="0"
							:maxNumber="250"
							:initalValue="guest.length"
							:hasError="validation.length.error"
							:errorMessage="validation.length.message"
							:lightTheme="true"
							@number-input="
								val => {
									this.guest.length = val
								}
							"
						/>
					</div>
					<div class="cell small-12 medium-4">
						<NumberComponent
							:label="$t('newaccount.weight') + ':'"
							:placeholderValue="'75'"
							:mandatory="skirental"
							:hideButtons="true"
							:minNumber="0"
							:maxNumber="300"
							:hasError="validation.weight.error"
							:errorMessage="validation.weight.message"
							:initalValue="guest.weight"
							:lightTheme="true"
							@number-input="
								val => {
									this.guest.weight = val
								}
							"
						/>
					</div>
					<div class="cell small-12 medium-4">
						<NumberComponent
							:label="$t('newaccount.shoesize') + ':'"
							:placeholderValue="'T.ex 42'"
							:mandatory="skirental"
							:hideButtons="true"
							:minNumber="16"
							:maxNumber="50"
							:hasError="validation.shoeSize.error"
							:errorMessage="validation.shoeSize.message"
							:initalValue="guest.shoesize"
							:lightTheme="true"
							@number-input="
								val => {
									this.guest.shoesize = val
								}
							"
						/>
					</div>
				</div>
			</AccordionFieldset>
		</template>
		<template v-else>
			<div class="create-guest-form__wrapper grid-x grid-margin-x">
				<div class="cell small-12">
					<h4 class="create-guest-form__headline">
						{{ $t('companion.profileHeading') }}
					</h4>
				</div>

				<div class="cell small-12 medium-4">
					<NumberComponent
						:label="$t('newaccount.length') + ':'"
						:placeholderValue="'180'"
						:mandatory="skirental"
						:hideButtons="true"
						:minNumber="0"
						:maxNumber="250"
						:initalValue="guest.length"
						:hasError="validation.length.error"
						:errorMessage="validation.length.message"
						:lightTheme="false"
						@number-input="
							val => {
								this.guest.length = val
							}
						"
					/>
				</div>
				<div class="cell small-12 medium-4">
					<NumberComponent
						:label="$t('newaccount.weight') + ':'"
						:placeholderValue="'75'"
						:mandatory="skirental"
						:hideButtons="true"
						:minNumber="0"
						:maxNumber="300"
						:hasError="validation.weight.error"
						:errorMessage="validation.weight.message"
						:initalValue="guest.weight"
						:lightTheme="false"
						@number-input="
							val => {
								this.guest.weight = val
							}
						"
					/>
				</div>
				<div class="cell small-12 medium-4">
					<NumberComponent
						:label="$t('newaccount.shoesize') + ':'"
						:placeholderValue="'T.ex 42'"
						:mandatory="skirental"
						:hideButtons="true"
						:minNumber="16"
						:maxNumber="50"
						:hasError="validation.shoeSize.error"
						:errorMessage="validation.shoeSize.message"
						:initalValue="guest.shoesize"
						:lightTheme="false"
						@number-input="
							val => {
								this.guest.shoesize = val
							}
						"
					/>
				</div>
			</div>
		</template>

		<!-- Contact section -->
		<template v-if="modalView">
			<AccordionFieldset
				v-on:toggle="
					() => {
						this.showContactSection = !this.showContactSection
					}
				"
				:title="$t('companion.contactHeading')"
				:show="this.showContactSection"
				:hasMandatoryFields="skischoolOrActivities"
				:missingFields="countErrors(
					[
						this.validation.mobilephoneprefix.error, 
						this.validation.mobilephone.error
					],
					[
						guest.phoneprefix,
						guest.phone3
					]
				)"
				icon="pen"
				:small="true"
				:lightTheme="true"
			>
				<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<!-- Prefix -->
					<div class="cell small-4 medium-3">
						<InputComponent
							:label="$t('newaccount.countryPlaceholder') + ':'"
							:mandatory="skischoolOrActivities"
							:productMandatory="skischoolOrActivities"
							placeholderValue="+46"
							v-model="guest.phoneprefix"
							:type="'tel'"
							:hasError="validation.mobilephoneprefix.error"
							:errorMessage="validation.mobilephoneprefix.message"
							:lightTheme="true"
							@input-enter="retry()"
						></InputComponent>
					</div>

					<!-- Phone -->
					<div class="cell small-12 medium-6">
						<InputComponent
							:label="$t('newaccount.phoneNumberPlaceholder') + ':'"
							:mandatory="skischoolOrActivities"
							:productMandatory="skischoolOrActivities"
							:placeholderValue="$t('newaccount.phoneNumberPlaceholder')"
							v-model="guest.phone3"
							:type="'tel'"
							:hasError="validation.mobilephone.error"
							:errorMessage="validation.mobilephone.message"
							:lightTheme="true"
							@input-enter="retry()"
						></InputComponent>
					</div>
				</div>
			</AccordionFieldset>
		</template>
		<template v-else>
			<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12">
						<h4 class="create-guest-form__headline">
							{{ $t('companion.contactHeading') }}
						</h4>
					</div>

					<!-- Prefix -->
					<div class="cell small-4 medium-3">
						<InputComponent
							:label="$t('newaccount.countryPlaceholder') + ':'"
							:mandatory="skischoolOrActivities"
							:productMandatory="skischoolOrActivities"
							placeholderValue="+46"
							v-model="guest.phoneprefix"
							:type="'tel'"
							:hasError="validation.mobilephoneprefix.error"
							:errorMessage="validation.mobilephoneprefix.message"
							:lightTheme="false"
							@input-enter="retry()"
						></InputComponent>
					</div>

					<!-- Phone -->
					<div class="cell small-12 medium-6">
						<InputComponent
							:label="$t('newaccount.phoneNumberPlaceholder') + ':'"
							:mandatory="skischoolOrActivities"
							:productMandatory="skischoolOrActivities"
							:placeholderValue="$t('newaccount.phoneNumberPlaceholder')"
							v-model="guest.phone3"
							:type="'tel'"
							:hasError="validation.mobilephone.error"
							:errorMessage="validation.mobilephone.message"
							:lightTheme="false"
							@input-enter="retry()"
						></InputComponent>
					</div>
				</div>
		</template>

		<!-- Image section -->
		<template v-if="modalView">
			<AccordionFieldset
				v-on:toggle="
					() => {
						this.showImageSection = !this.showImageSection
					}
				"
				:title="$t('companion.imageUploadHeading')"
				:show="this.showImageSection"
				:hasMandatoryFields="photoMandatory"
				:missingFields="countErrors([this.validation.image.error])"
				icon="pen"
				:small="true"
				:lightTheme="true"
			>
				<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12">
						<!-- Image upload -->
						<ImageUpload
							:mandatory="photoMandatory"
							:initalValue="guest.image"
							:hasError="validation.image.error"
							:errorMessage="validation.image.message"
							@image-save="
								val => {
									setImage(val)
								}
							"
						></ImageUpload>
					</div>
				</div>
			</AccordionFieldset>
		</template>
		<template v-else>
			<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12">
						<h4 class="create-guest-form__headline">
							{{ $t('companion.imageUploadHeading') }}
						</h4>
					</div>
					<div class="cell small-12">
						<!-- Image upload -->
						<ImageUpload
							:mandatory="photoMandatory"
							:initalValue="guest.image"
							:hasError="validation.image.error"
							:errorMessage="validation.image.message"
							@image-save="
								val => {
									setImage(val)
								}
							"
						></ImageUpload>
					</div>
				</div>
		</template>

		<!-- Keycard section -->
		<template v-if="modalView">
			<AccordionFieldset
				v-on:toggle="
					() => {
						this.showKeycardSection = !this.showKeycardSection
					}
				"
				:title="$t('companion.keycardHeading')"
				:show="this.showKeycardSection"
				:hasMandatoryFields="false"
				icon="pen"
				:small="true"
				:lightTheme="true"
			>
				<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12 medium-9">
						<!-- Key number input -->
						<InputComponent
							:label="$t('companion.keycardInputLabel') + ':'"
							:placeholderValue="$t('companion.keycardInputText')"
							:hasError="showKeycardError"
							v-model="guest.keynumber"
							:type="'tel'"
							:lightTheme="true"
							@input-enter="retry()"
						></InputComponent>
						<p v-if="showKeycardError" class="keycard-error">
							{{ $t('companion.keycardError') }}
						</p>
					</div>
					<!-- <div class="cell small-12">
						<p class="create-guest-form__keycard-info">
							{{ $t('companion.keycardInputInfo') }}
						</p>
					</div> -->
				</div>
			</AccordionFieldset>
		</template>
		<template v-else>
			<div class="create-guest-form__wrapper grid-x grid-margin-x">
					<div class="cell small-12">
						<h4 class="create-guest-form__headline">
							{{ $t('companion.keycardHeading') }}
						</h4>
					</div>
					<div class="cell small-12 medium-9">
						<!-- Key number input -->
						<InputComponent
							:label="$t('companion.keycardInputLabel') + ':'"
							:placeholderValue="$t('companion.keycardInputText')"
							:hasError="showKeycardError"
							v-model="guest.keynumber"
							:type="'tel'"
							:lightTheme="false"
							@input-enter="retry()"
						></InputComponent>
						<p v-if="showKeycardError" class="keycard-error">
							{{ $t('companion.keycardError') }}
						</p>
					</div>
					<!-- <div class="cell small-12">
						<p class="create-guest-form__keycard-info">
							{{ $t('companion.keycardInputInfo') }}
						</p>
					</div> -->
				</div>
		</template>

		<!-- Validation -->
		<div class="grid-x grid-margin-x">
			<div v-if="apiResponseSuccess === false" class="cell small-12 medium-6">
				<div class="api-response-error">
					<p>
						{{ $t('newaccount.emailAlreadyRegisteredText') }}
						<Button @click="retry()">
							{{ $t('newaccount.tryAgainButton') }}
						</Button>
					</p>
				</div>
			</div>
		</div>

		<!-- Buttons -->
		<div class="create-guest-form__buttons grid-x grid-margin-x">
			<div v-if="!valid" class="cell small-12">
				<p class="error">{{ $t('companion.errorText') }}</p>
			</div>

			<div class="cell small-12 create-guest-form__border-top">
				<Button
					v-if="edit"
					@click.native="validateGuest"
					:loading="loading"
					size="small"
					class="create-guest-form__button"
				>
					{{ $t('newaccount.updateGuest') }}
				</Button>
				<Button
					v-else
					size="small"
					@click.native="validateGuest"
					:loading="loading"
					class="create-guest-form__button"
				>
					{{ $t('newaccount.addGuest') }}
				</Button>
				<!--<Button
					v-if="guest.guestlid != 0"
					size="small"
					@click.native="removeGuest"
					color="red"
					:loading="loading"
					class="create-guest-form__button"
				>
					{{ $t('newaccount.removeGuest') }}
				</Button>-->
			</div>
		</div>
		<!--</form>-->

		<!-- Success text -->
		<div v-if="showSuccess" class="grid-x grid-margin-x">
			<div class="cell small-12">
				<p v-if="edit" class="create-guest-form__success">
					{{ $t('companion.updatedSuccessText') }}
				</p>
				<p v-else class="create-guest-form__success">
					{{ $t('companion.createdSuccessText') }}
				</p>
			</div>
		</div>
	</section>
</template>

<script>
import {
	testBasic,
	testPhoneNumber,
	testPrefix,
	testBirthdate,
	testLength,
	testWeight,
	testShoeSize,
	testImage,
	validationErrors
} from '../../../utilities/validation'

import '../../../utilities/helper.js'
import Button from '../../../components/atoms/Button'
import InputComponent from '../../../components/atoms/InputComponent'
import NumberComponent from '../../../components/atoms/NumberComponent'
import ImageUpload from '../../../components/atoms/ImageUpload'
import BirthDate from '../../../components/molecules/BirthDate'
//import Accordion from '../../../components/molecules/Accordion'
import AccordionFieldset from '../../../components/molecules/AccordionFieldset'
import store from '../../../vueX/store'

export default {
	name: 'Guest_Form',
	store,
	components: {
		InputComponent,
		Button,
		NumberComponent,
		ImageUpload,
		BirthDate,
		//Accordion,
		AccordionFieldset
	},
	data: () => {
		return {
			guest: {
				guestlid: 0,
				addrclid: store.state.user.id,
				name: '',
				lastname: '',
				phoneprefix: '',
				phone3: '',
				birthdate: '',
				length: '',
				weight: '',
				shoesize: '',
				keynumber: '',
				image: '',
				imageBlob: ''
			},

			keycardValid: false,
			showSuccess: false,
			showKeycardSuccess: false,
			showKeycardError: false,
			loading: false,
			consent: false,
			validation: validationErrors,
			apiResponse: null,
			guestId: null,
			skischool: false,
			skirental: false,
			skipass: false,
			//skipassPhotoMandatory: false,
			skischoolOrActivities: false,
			valid: true,
			showPersonalSection: true,
			showSizeSection: true,
			showContactSection: true,
			showImageSection: true,
			showKeycardSection: true
		}
	},
	props: {
		guestlid: {
			type: Number
		},
		showEditModal: {
			type: Boolean
		},
		modalView: {
			type: Boolean,
			default: false
		},
		guestType: {
			type: String,
			default: ''
		},
		photoMandatory: {
			type: Boolean,
			default: false
		},
		edit: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		countErrors(errorArray, valueArray) {
			//console.log(errorArray)
			var amountOfErrors = 0

			//Check if empty
			if(valueArray && valueArray.length > 0) {
				valueArray.forEach(function (value) {
					if (value === '' || value == 0) {
						++amountOfErrors
					}
				})

			//Check if errors
			} else {
				errorArray.forEach(function (error) {
					if (error === true) {
						++amountOfErrors
					}
				})
			}

			return amountOfErrors
		},
		setImage(val) {
			this.guest.image = val
		},
		onChangeDate(val) {
			this.guest.birthdate = val
		},
		addOrUpdateGuest() {
			this.loading = true
			store
				.dispatch('PUT_GUEST', this.guest)
				.then(success => {
					if (success) {
						const lastItem =
							store.state.user.guests[store.state.user.guests.length - 1]
						this.$emit('close')
						this.setGuest(lastItem.guestlid)
						this.showSuccess = true
						if (!this.edit) {
							this.guest = {
								guestlid: 0,
								addrclid: store.state.user.id,
								name: '',
								lastname: '',
								phoneprefix: '',
								phone3: '',
								birthdate: '',
								length: '',
								weight: '',
								shoesize: '',
								keynumber: '',
								image: ''
							}
							this.$emit('hide-accordion')
						}
					}
					this.loading = false
				})
				.catch(function (error) {
					window.console.error(error)
					this.loading = false
				})
		},

		validateGuest() {
			this.showSuccess = false
			this.showKeycardSuccess = false
			this.showKeycardError = false
			this.$emit('empty')
			if (this.validate()) {
				store.dispatch('REFRESH_TOKEN_BEFORE_REQUEST').then(response => {
					if (response) {
						if (this.guest.keynumber == '') {
							this.addOrUpdateGuest()
						} else {
							this.loading = true
							this.showKeycardSuccess = false
							this.showKeycardError = false
							store
								.dispatch('GET_KEY_CARD_VALIDATION', this.guest.keynumber)
								.then(success => {
									if (success.length && success[0].valid) {
										this.keycardValid = true
										this.loading = false
										this.showKeycardSuccess = true
										this.addOrUpdateGuest()
									} else {
										this.loading = false
										this.showKeycardError = true
										this.keycardValid = false
									}
								})
								.catch(function (error) {
									window.error(error)
									this.loading = false
								})
						}
					} else {
						this.loading = false
					}
				})
			}
		},

		removeGuest() {
			this.loading = true

			store.dispatch('REFRESH_TOKEN_BEFORE_REQUEST').then(response => {
				if (response) {
					store
						.dispatch('DELETE_GUEST', this.guest)
						.then(success => {
							if (success) {
								this.loading = false
								this.$router.push({ name: 'guests' })
							} else {
								this.loading = false
							}
						})
						.catch(function (error) {
							window.error(error)
							this.loading = false
						})
				} else {
					this.loading = false
				}
			})
		},
		setGuest(val) {
			this.$emit('set-guest', val)
		},

		retry() {
			store.dispatch('CHANGE_API_RESP_STATUS', null)
			this.loading = false
		},
		validate() {
			let approved = true
			// reset errors and message before testing a new validation
			Object.keys(this.validation).map(key => {
				this.validation[key].error = false
				this.validation[key].message = ''
			})

			// ↓ Always mandatory ↓
			if (!testBasic(this.guest.name)) {
				this.validation.name.error = true
				this.validation.name.message = this.$t('errorText.name')
				approved = false
			}

			if (!testBasic(this.guest.lastname)) {
				this.validation.lastname.error = true
				this.validation.lastname.message = this.$t('errorText.lastname')
				approved = false
			}

			if (!testBirthdate(this.guest.birthdate)) {
				this.validation.birthdate.error = true
				this.validation.birthdate.message = this.$t('errorText.birthdate')
				approved = false
			}
			// ↑ Always mandatory ↑

			if (this.guestType == 'skipass' && this.photoMandatory) {
				if (!testImage(this.guest.image)) {
					this.validation.image.error = true
					this.validation.image.message = this.$t('errorText.image')
					approved = false
				}
			}

			if (this.guestType == 'skirental') {
				if (!testLength(this.guest.length)) {
					this.validation.length.error = true
					this.validation.length.message = this.$t('errorText.length')
					approved = false
				}
				if (!testWeight(this.guest.weight)) {
					this.validation.weight.error = true
					this.validation.weight.message = this.$t('errorText.weight')
					approved = false
				}
				if (!testShoeSize(this.guest.shoesize)) {
					this.validation.shoeSize.error = true
					this.validation.shoeSize.message = this.$t('errorText.shoesize')
					approved = false
				}
			}

			if (this.guestType == 'skischool' || this.guestType == 'activities') {
				if (!testPrefix(this.guest.phoneprefix)) {
					this.validation.mobilephoneprefix.error = true
					this.validation.mobilephoneprefix.message = this.$t(
						'errorText.prefix'
					)
					approved = false
				}

				if (!testPhoneNumber(this.guest.phone3)) {
					this.validation.mobilephone.error = true
					this.validation.mobilephone.message = this.$t('errorText.phoneNumber')
					approved = false
				}
			}
			this.valid = approved
			return approved
		}
	},

	computed: {
		apiResponseSuccess() {
			if (this.$store.state.apiResponseStatus === 'User exists') {
				return false
			} else {
				return true
			}
		},
		mandatorySpecific(val) {
			if (val == 'skipass' || val == 'skirental') {
				return true
			} else {
				return false
			}
		},
		currentGuest() {
			if (store.state.user.guests != null) {
				return store.state.user.guests.filter(
					each => each.guestlid == this.guestlid
				)
			} else {
				return []
			}
		}
	},
	created() {
		Object.keys(this.validation).map(key => {
			this.validation[key].error = false
			this.validation[key].message = ''
		})

		if (this.currentGuest.length > 0) {
			this.guest = { ...this.currentGuest[0] }
			if (this.guest.length == 0) {
				this.guest.length = ''
			}
			if (this.guest.weight == 0) {
				this.guest.weight = ''
			}
			if (this.guest.shoesize == 0) {
				this.guest.shoesize = ''
			}
		}

		//activates mandatory fields
		if (this.guestType == 'skirental') {
			this.skirental = true

			//Only open accordions with mandatory fields
			this.showPersonalSection = true
			this.showSizeSection = true
			this.showContactSection = false
			this.showImageSection = false
			this.showKeycardSection = false

		}
		if (this.guestType == 'skischool') {
			this.skischool = true

			//Only open accordions with mandatory fields
			this.showPersonalSection = true
			this.showSizeSection = false
			this.showContactSection = true
			this.showImageSection = false
			this.showKeycardSection = false
		}
		if (this.guestType == 'skipass') {
			this.skipass = true

			//Only open accordions with mandatory fields
			this.showPersonalSection = true
			this.showSizeSection = false
			this.showContactSection = false
			this.showImageSection = this.photoMandatory
			this.showKeycardSection = true
		}
		// if (this.guestType == 'skipass' && this.photoMandatory) {
		// 	this.skipassPhotoMandatory = true
		// }
		if (this.guestType == 'skischool' || this.guestType == 'activities') {
			this.skischoolOrActivities = true

			//Only open accordions with mandatory fields
			this.showPersonalSection = true
			this.showSizeSection = false
			this.showContactSection = true
			this.showImageSection = false
			this.showKeycardSection = false
		}

		this.keycardValid = this.guest.keynumber ? true : false
	}
}
</script>

<style scoped lang="scss">
.error {
	color: $colorRed;
}
.keycard-error {
	color: $colorRed;
	position: relative;
	top: -20px;
	color: #e15b5b;
    display: inline-block;
    font-size: 10px;
    line-height: 15px;
}
.api-response-error {
	padding: 20px;
	background-color: $colorDarkerRed;

	p,
	a {
		font-size: 15px;
		color: $colorWhite;
	}

	a {
		text-decoration: underline;
	}
}

.create-guest-form {
	margin-bottom: 40px;
	&__headline {
		border-top: 1px solid #ccc;
		margin-top: 20px;
		padding: 20px 0 10px 0;
		//font-size: 20px;
		&--no-margin {
			margin-top: 0;
		}
	}
	&__buttons {
		text-align: right;
		margin-top: 20px;
	}
	&__button {
		display: inline-block;
		margin-right: 20px;
		width: 100%;

		@include for-tablet-portrait-up {
			width: auto;
		}
	}
	&__border-top {
		border-top: 1px solid #ccc;
		padding: 40px 0;
		@include for-tablet-portrait-up {
			padding: 40px 0 0 0;
		}
	}
	&__wrapper {
		@include for-tablet-portrait-up {
			width: 100%;
		}
	}
	&__title {
		margin: 20px 0 30px;
	}
	&__text {
		margin: 20px 0;
	}

	&__title {
		/*font-size: 28px;
		line-height: 1.1;*/
		svg {
			display: inline-block;
			vertical-align: middle;
			width: 30px;
			height: 30px;
			margin-right: 10px;
			fill: #020304;
		}
		span {
			display: inline-block;
			vertical-align: middle;
		}
	}

	&__back {
		color: #7f8ba1;
		font-size: 16px;
		padding-left: 48px;
		span:last-child {
			padding-left: 20px;
		}
	}

	.gutter-top {
		margin-top: 50px;
	}

	&__prefix .input__container {
		margin-right: 20px;
	}

	&__consent-text {
		text-align: left;
		color: $colorWhite;
		margin-left: 10px;
		&:hover {
			cursor: pointer;
		}
	}

	&__consent-error {
		margin-top: -10px;
		&--white {
			color: $colorWhite;
		}
	}
	&__verify {
		display: flex;
		align-items: center;
		margin-top: 49px;
	}
	// &__keycard p {
	// 	font-size: 10px;
	// 	color:
	// }

	&__keycard-info {
		font-size: 12px;
		line-height: 1.2;
		font-style: italic;
	}
}

.checkbox {
	display: flex;
	position: relative;
	align-items: center;
	width: 100%;
	&--disabled {
		opacity: 0.5;
		pointer-events: none;
	}
	&__input {
		position: absolute;
		visibility: hidden;
		/*&:checked ~ .input__icon:after {
      transform: scale(1);
    }*/
		+ span > span {
			display: none;
		}
		&:checked {
			+ span {
				background-color: $colorPrimary7;
			}
			+ span > span {
				display: block;
			}
		}
	}
	&__box {
		display: inline-block;
		width: 18px;
		height: 18px;
		box-sizing: border-box;
		border: 1px solid $colorPrimary3;
		box-shadow: inset 0 1px 3px 0 rgba(148, 158, 177, 0.1);
		background-color: rgba(233, 235, 239, 0.6);
		border-radius: 3px;
		transition: all 0.3s ease;
		margin-right: 5px;
		position: relative;
		svg {
			width: 12px;
			height: 12px;
		}
		> span {
			position: absolute;
			top: 50%;
			left: 50%;
			transform: translate(-50%, -50%);
		}
	}

	&__title {
		align-items: center;
		display: flex;
		color: $colorPrimary;
		font-size: 16px;
		line-height: 18px;
	}
}
</style>
